import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { getToken, BASE } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";

// Maps a broadcast entity to the react-query keys it should refresh.
const ENTITY_KEYS = {
  posts: [["posts"], ["post"], ["home"], ["for-you"]],
  events: [["events"], ["event"], ["home"], ["for-you"], ["saved-events"]],
  jobs: [["jobs"], ["job"], ["home"], ["for-you"], ["matches"]],
  members: [["members"], ["home"], ["for-you"], ["suggestions"], ["admin-users"], ["admin-stats"]],
  admin_members: [["admin-users"], ["admin-stats"]],
  messages: [["conversations"], ["messages"], ["chat"]],
  conversations: [["conversations"]],
  admin_pending: [["admin-pending"], ["admin-stats"], ["admin-pending-badge"], ["admin-badges"]],
  social: [["social-posts"], ["biz-mkt-events"], ["biz-mkt"], ["admin-spotlights"]],
};

const HEARTBEAT_MS = 25000;   // send a ping this often
const PONG_TIMEOUT_MS = 60000; // force-reconnect if no server traffic within this window
const MAX_BACKOFF_MS = 30000;

// App-wide realtime: one resilient WebSocket that invalidates the relevant caches
// when any user creates/updates content. Auto-reconnects with exponential backoff,
// heartbeats to detect dead/half-open sockets, and re-authenticates on reconnect.
export default function useRealtime() {
  const qc = useQueryClient();
  const { refresh } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    let ws;
    let closed = false;
    let retry;
    let heartbeat;
    let backoff = 1000;
    let lastSeen = Date.now();
    let hadDrop = false;

    const clearTimers = () => { clearTimeout(retry); clearInterval(heartbeat); };

    const scheduleReconnect = () => {
      if (closed) return;
      clearTimers();
      const delay = Math.min(backoff, MAX_BACKOFF_MS) + Math.floor(Math.random() * 500);
      retry = setTimeout(connect, delay);
      backoff = Math.min(backoff * 2, MAX_BACKOFF_MS);
    };

    const connect = () => {
      // Re-read the latest token every attempt so we always connect with a valid session.
      const token = getToken();
      if (!token) { scheduleReconnect(); return; }
      const wsUrl = `${BASE.replace(/^http/, "ws")}/ws/app?token=${encodeURIComponent(token)}`;
      try { ws = new WebSocket(wsUrl); } catch { scheduleReconnect(); return; }

      ws.onopen = () => {
        backoff = 1000; // reset backoff on a healthy connection
        lastSeen = Date.now();
        // Re-authenticate + resync any updates missed while disconnected.
        if (hadDrop) {
          hadDrop = false;
          refresh?.();
          qc.invalidateQueries();
        }
        clearInterval(heartbeat);
        heartbeat = setInterval(() => {
          if (Date.now() - lastSeen > PONG_TIMEOUT_MS) {
            try { ws.close(); } catch { /* noop */ }
            return;
          }
          try { ws.send(JSON.stringify({ type: "ping" })); } catch { /* noop */ }
        }, HEARTBEAT_MS);
      };

      ws.onmessage = (ev) => {
        lastSeen = Date.now();
        let data;
        try { data = JSON.parse(ev.data); } catch { return; }
        if (data.type === "identity_changed") {
          // Admin changed this member's role/permissions, re-fetch identity + resync.
          refresh?.();
          qc.invalidateQueries();
          return;
        }
        if (data.type === "admin_alert") {
          // A member→admin submission just arrived (ticket, verification, business app,
          // flag, contact). Refresh every admin badge/count cache instantly...
          ["admin-badges", "admin-pending", "admin-pending-badge", "admin-stats",
           "admin-inbox-badge", "admin-inbox", "admin-reports"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
          // ...and show a live pop-up with the member + subject + a View link to the item.
          toast(data.title || "New member message", {
            description: data.subtitle || undefined,
            duration: 8000,
            action: data.link ? { label: "View", onClick: () => navigate(data.link) } : undefined,
          });
          return;
        }
        if (data.type !== "content_changed") return; // pong / other frames just refresh liveness
        const seen = new Set();
        (data.entities || []).forEach((entity) => {
          (ENTITY_KEYS[entity] || [[entity]]).forEach((key) => {
            const id = key.join("|");
            if (seen.has(id)) return;
            seen.add(id);
            qc.invalidateQueries({ queryKey: key });
          });
        });
      };

      ws.onclose = () => { hadDrop = true; scheduleReconnect(); };
      ws.onerror = () => { try { ws.close(); } catch { /* noop */ } };
    };

    connect();
    return () => { closed = true; clearTimers(); try { ws && ws.close(); } catch { /* noop */ } };
  }, [qc, refresh, navigate]);
}
